import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { pick } from "lodash"

import getErrorMessage from "utils/getErrorMessage"

import { ReactComponent as IconEditUser } from "../../assets/svg/modal-icon-edit-columns.svg"
import { DADItem } from "../../components/common/DADItem"
import { ModalHeader } from "../../components/common/ModalHeader"
import { ModalLayout } from "../../layouts/ModalLayout"
import { useLazyGetTableColumnsQuery, useLazyUpdateTableColumnsQuery } from "../../store/api/dashboardApi"
import { ApiTypes, UITypes } from "../../types"

import styles from "./index.module.scss"

interface Props {
  tableName: string
  tableHeaderCells: UITypes.TableHeaderCell[]
  onCancel: () => void
}

export const EditColumnsModal = ({ tableName, tableHeaderCells, onCancel }: Props) => {
  const [items, setItems] = useState<UITypes.TableHeaderCell[]>([])
  const [triggerUpdateTableColumnsQuery, { isFetching }] = useLazyUpdateTableColumnsQuery()
  const [triggerGetTableColumnsQuery] = useLazyGetTableColumnsQuery()
  const draggableWrapperRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState(0)
  const [threshold, setThreshold] = useState<{ x: number; y: number } | undefined>(undefined)

  const fixedColumns = useMemo(
    () => tableHeaderCells.filter(cell => ["group_toggle", "checkbox"].includes(cell.name)),
    [tableHeaderCells],
  )

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const preventMovement: Modifier = useCallback(
    ({ transform }) => {
      if (
        draggableWrapperRef.current?.scrollHeight &&
        draggableWrapperRef.current.scrollHeight > containerHeight &&
        containerHeight > 0
      ) {
        if (threshold === undefined) {
          setThreshold(prev => (prev ? prev : { x: 0, y: 0 }))
          draggableWrapperRef.current.scrollTop = draggableWrapperRef.current.offsetHeight + 180
        }
      } else {
        setThreshold(prev => (!prev ? prev : undefined))
      }

      return transform
    },
    [containerHeight, threshold],
  )

  const onCancelClick = () => {
    onCancel()
  }

  const handleDragStart = () => {
    setThreshold(undefined)
    const initialHeight = draggableWrapperRef.current?.scrollHeight || 0
    setContainerHeight(initialHeight)
  }

  function handleDragEnd(event: DragEndEvent) {
    if (!items.length) return

    const { active, over } = event

    if (over && active.id !== over.id) {
      setItems(items => {
        const oldIndex = items.findIndex(item => item.name === active.id)
        const newIndex = items.findIndex(item => item.name === over.id)

        const newItems = arrayMove(items!, oldIndex!, newIndex!)
        return [...newItems]
      })
    }
  }

  const onVisibilityChange = (value: string) => {
    const result = items.map(item => {
      if (item.name === value) {
        return { ...item, ...{ visibility: !item.visibility } }
      }
      return item
    })

    setItems(result)
  }

  const sendRequest = async () => {
    try {
      const fixedColumnsConfig = fixedColumns.map(item =>
        pick(item, ["label", "name", "visibility"]),
      ) as ApiTypes.Model.TableColumn[]

      const reOrderableColumnsConfig = items.map(item =>
        pick(item, ["label", "name", "visibility"]),
      ) as ApiTypes.Model.TableColumn[]

      const config = [...fixedColumnsConfig, ...reOrderableColumnsConfig]

      const res = await triggerUpdateTableColumnsQuery({
        body: {
          list_name: tableName as ApiTypes.Model.PageTableName,
          config,
        },
      })
      const error = getErrorMessage(res?.error)

      if (res?.isSuccess) {
        toast.success("Table columns have been successfully updated!")
        triggerGetTableColumnsQuery({ list_name: tableName as ApiTypes.Model.PageTableName })
        onCancel()
      }
      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onSubmit = async () => {
    sendRequest()
  }

  useEffect(() => {
    setItems(
      tableHeaderCells
        .filter(item => !["group_toggle", "checkbox"].includes(item.name))
        .map(item => ({ ...item, label: item.label || item.title })),
    )
  }, [tableHeaderCells])

  return (
    <>
      {createPortal(
        <ModalLayout
          width="760px"
          confirmButtonText="Save"
          buttonSize="auto"
          onConfirm={onSubmit}
          onCancel={onCancelClick}
          isFetching={isFetching}
          className={styles.editColumnsModal}
        >
          <ModalHeader
            title="Edit columns"
            subtitle="Customize your view by hiding unnecessary columns and reordering them to suit your needs."
          >
            <IconEditUser />
          </ModalHeader>
          <div ref={draggableWrapperRef} className={styles.draggableWrapper}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              modifiers={[preventMovement]}
              autoScroll={{ threshold: threshold }}
            >
              {Boolean(items.length) && (
                <SortableContext items={items.map(item => item.name)} strategy={verticalListSortingStrategy}>
                  {items.map(item => (
                    <div key={item.name}>
                      <DADItem {...item} onVisibilityChange={onVisibilityChange} />
                    </div>
                  ))}
                </SortableContext>
              )}
            </DndContext>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
